import { useState, type FormEvent } from 'react'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { authClient } from '@/lib/auth-client'
import { publicConfig } from '@/lib/public-config'
import { queryClient } from '@/lib/query-client'
import { resolveRedirect } from '@/lib/redirect'
import logo from '@/assets/edclub-logo.png'

type Search = { redirectTo?: string; error?: string }

export const Route = createFileRoute('/login')({
	validateSearch: (search: Record<string, unknown>): Search => ({
		redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
		// Set by Better Auth when Google sign-in fails (errorCallbackURL).
		error: typeof search.error === 'string' ? search.error : undefined
	}),
	// Already signed in: skip the form. (This is also how other tools refresh an expired
	// session JWT, and where Google sign-in lands: they bounce through here and the
	// getSession call re-issues the cookie.)
	beforeLoad: async ({ search }) => {
		const { data } = await authClient.getSession()
		if (!data) return
		const target = await resolveRedirect(search.redirectTo)
		if ('href' in target) {
			window.location.replace(target.href)
			return new Promise<never>(() => {}) // keep the router pending while the browser leaves
		}
		throw redirect({ to: target.path, replace: true })
	},
	// Email + password only in local development; Google wherever it's configured (see server auth.ts).
	loader: async () => {
		const config = await publicConfig()
		return { ...config.authMethods, unavailable: Boolean(config.unavailable) }
	},
	component: Login
})

const googleErrors: Record<string, string> = {
	unable_to_get_user_info: 'Use your edclub Google Workspace account.',
	access_denied: 'Google sign-in was cancelled.'
}

function Login() {
	const router = useRouter()
	const methods = Route.useLoaderData()
	const { redirectTo, error: oauthError } = Route.useSearch()
	const [mode, setMode] = useState<'sign-in' | 'sign-up'>('sign-in')
	const [error, setError] = useState(
		oauthError && (googleErrors[oauthError] ?? `Google sign-in failed (${oauthError.replaceAll('_', ' ')}).`)
	)
	const [pending, setPending] = useState<'email' | 'google'>()
	const signIn = mode === 'sign-in'

	const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const form = new FormData(e.currentTarget)
		const email = String(form.get('email'))
		const password = String(form.get('password'))

		setPending('email')
		setError(undefined)
		const { error } = signIn
			? await authClient.signIn.email({ email, password })
			: await authClient.signUp.email({ email, password, name: String(form.get('name')) })
		setPending(undefined)

		if (error) return setError(error.message ?? 'Something went wrong')
		queryClient.clear()
		await router.invalidate() // re-runs beforeLoad, which now sees the session and redirects
	}

	const signInWithGoogle = async () => {
		setPending('google')
		setError(undefined)
		// Come back to /login, which validates redirectTo (incl. other tools) like a normal sign-in.
		const back = `/login${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`
		const { error } = await authClient.signIn.social({ provider: 'google', callbackURL: back, errorCallbackURL: back })
		if (error) {
			setPending(undefined)
			setError(error.message ?? 'Google sign-in failed')
		}
	}

	const noMethods = !methods.unavailable && !methods.emailPassword && !methods.google

	return (
		<div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-[radial-gradient(1200px_600px_at_100%_-10%,#fff1de,transparent_60%),radial-gradient(900px_500px_at_-10%_110%,#eef2f7,transparent_60%)] p-6">
			<Card className="w-full max-w-sm gap-6 py-8 shadow-[0_10px_30px_rgba(18,41,71,0.12)]">
				<CardHeader className="gap-6 px-8">
					<img src={logo} alt="edclub" className="h-10 w-auto self-start" />
					<div className="grid gap-1">
						<CardTitle className="text-xl font-bold text-[#122947]">
							{signIn ? 'Admin sign in' : 'Create admin account'}
						</CardTitle>
						<CardDescription>
							{methods.unavailable
								? 'Welcome back.'
								: methods.emailPassword
								? signIn
									? 'Welcome back. Sign in to continue.'
									: 'Set up your account to get started.'
								: 'Sign in with your edclub Google account.'}
						</CardDescription>
					</div>
				</CardHeader>
				<CardContent className="grid gap-6 px-8">
					{methods.google && (
						<Button
							type="button"
							variant="outline"
							size="lg"
							className="w-full font-semibold"
							disabled={!!pending}
							onClick={signInWithGoogle}
						>
							{pending === 'google' ? <Spinner /> : <GoogleIcon />}
							Continue with Google
						</Button>
					)}

					{methods.google && methods.emailPassword && (
						<div className="flex items-center gap-3 text-xs text-muted-foreground">
							<span className="h-px flex-1 bg-border" />
							or, in local development
							<span className="h-px flex-1 bg-border" />
						</div>
					)}

					{methods.emailPassword && (
						<form onSubmit={onSubmit} className="grid gap-4">
							{!signIn && (
								<div className="grid gap-2">
									<Label htmlFor="name">Name</Label>
									<Input id="name" name="name" autoComplete="name" required />
								</div>
							)}
							<div className="grid gap-2">
								<Label htmlFor="email">Email</Label>
								<Input id="email" name="email" type="email" autoComplete="email" required />
							</div>
							<div className="grid gap-2">
								<Label htmlFor="password">Password</Label>
								<Input
									id="password"
									name="password"
									type="password"
									autoComplete={signIn ? 'current-password' : 'new-password'}
									minLength={8}
									required
								/>
							</div>
							<Button type="submit" size="lg" className="w-full font-semibold" disabled={!!pending}>
								{pending === 'email' && <Spinner />}
								{signIn ? 'Sign in' : 'Create account'}
							</Button>
						</form>
					)}

					{error && (
						<p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
							{error}
						</p>
					)}

					{methods.unavailable && (
						<div className="grid gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
							Couldn't load sign-in options.
							<Button type="button" variant="outline" size="sm" onClick={() => router.invalidate()}>
								Try again
							</Button>
						</div>
					)}

					{noMethods && (
						<p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
							Sign-in isn't configured. Set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.
						</p>
					)}

					{methods.emailPassword && (
						<p className="text-center text-sm text-muted-foreground">
							{signIn ? 'New here?' : 'Already have an account?'}{' '}
							<button
								type="button"
								className="font-semibold text-[#42576e] hover:text-primary"
								onClick={() => (setMode(signIn ? 'sign-up' : 'sign-in'), setError(undefined))}
							>
								{signIn ? 'Create an account' : 'Sign in'}
							</button>
						</p>
					)}
				</CardContent>
			</Card>
			<p className="text-xs text-muted-foreground">© {new Date().getFullYear()} edclub</p>
		</div>
	)
}

function GoogleIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden className="size-4">
			<path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.81Z" />
			<path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.88-3A7.2 7.2 0 0 1 12 19.25a7.16 7.16 0 0 1-6.72-4.95H1.27v3.1A12 12 0 0 0 12 24Z" />
			<path fill="#FBBC05" d="M5.28 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.27a12 12 0 0 0 0 10.8l4.01-3.1Z" />
			<path fill="#EA4335" d="M12 4.75c1.76 0 3.34.6 4.59 1.8l3.44-3.44A11.97 11.97 0 0 0 1.27 6.6l4.01 3.1A7.16 7.16 0 0 1 12 4.75Z" />
		</svg>
	)
}
